import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind-merge only knows Tailwinds own theme keys. Samklangs keys from
 * `@samklang/design-tokens/tailwind-theme` are invisible to it, and for the
 * `text-*` namespace that is not merely a missed merge but a silent drop:
 * `text-body-sm` is read as a color, so `cn("text-body-sm", "text-foreground")`
 * would keep the color and throw the size away.
 *
 * Teaching it the custom keys makes each utility land in the group it belongs
 * to, so a size only ever conflicts with another size and a color with another
 * color. Every key below is defined in `tailwind-theme.css`. Add a key here
 * whenever one is added there.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: ["display", "title", "body-sm", "label"] }],
      leading: [{ leading: ["display", "title"] }],
      /*
       * `rounded`, not `border-radius`: tailwind-merge names the group after
       * the utility prefix, and an unknown group name is accepted silently
       * without ever taking part in a conflict, so `cn("rounded-card",
       * "rounded-none")` would keep both.
       */
      rounded: [{ rounded: ["card"] }],
      shadow: [
        {
          shadow: [
            "button",
            "button-lg",
            "button-secondary",
            "button-danger",
            "card",
            "float",
          ],
        },
      ],
      tracking: [{ tracking: ["snug", "label"] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
